import { FALLBACK_CAPTURE_FPS, captureFloorFromConstraints, trackMatchesVideoConstraints,
  trackMeetsCaptureProfile, videoConstraintsForShadowcam, type ShadowcamCaptureProfile } from '../../shadowcamCaptureProfiles';

export type CaptureVideoOptions = {
  verifyProfile?: ShadowcamCaptureProfile;
  shadowcamName?: string;
  isCurrent?: () => boolean;
  hostDeadlineMs?: number;
};
export interface PreparedVideoCapture {
  readonly id: number;
  readonly previousTrack: MediaStreamTrack | null;
  readonly track: MediaStreamTrack;
}
export interface RetryableCapturePreparationError extends Error {
  code: 'capture-preparation-failed';
  retryable: true;
}
type Preparation = { value: PreparedVideoCapture; generation: number };

/** Owns video acquisition/publication separately from sender-safe track retirement. */
export class VideoCaptureLifecycle {
  private current: MediaStreamTrack | null = null;
  private generation = 0;
  private nextId = 0;
  private prepared = new Map<number, Preparation>();
  private readonly committed = new WeakSet<PreparedVideoCapture>();
  private readonly stopped = new WeakSet<MediaStreamTrack>();
  constructor(private readonly deps: {
    acquire: (constraints: MediaTrackConstraints) => Promise<MediaStream>;
    changed: (track: MediaStreamTrack | null) => void;
    nowMs?: () => number;
  }) {}

  getVideoTrack(): MediaStreamTrack | null { return this.current; }

  async captureVideo(constraints: MediaTrackConstraints, options: CaptureVideoOptions = {}): Promise<MediaStreamTrack | null> {
    try {
      const prepared = await this.prepareVideo(constraints, options);
      if (!prepared) return null;
      let track: MediaStreamTrack;
      try { track = this.commitPreparedVideo(prepared, options.isCurrent); }
      catch (error) { this.discardPreparedVideo(prepared); throw error; }
      if (prepared.previousTrack && prepared.previousTrack !== track) this.retireVideoTrack(prepared.previousTrack);
      return track;
    } catch (error) {
      if (options.verifyProfile || captureFloorFromConstraints(constraints) || options.isCurrent?.() === false) throw error;
      return null;
    }
  }

  /**
   * Prepare a reusable or newly acquired track without replacing the current track.
   * A profile allows two attempts by default, retrying exact dimensions while only
   * preferring 60 FPS (the device picks the rate it supports at that size);
   * maxAcquisitionAttempts can restrict this to one. hostDeadlineMs is absolute in
   * the injected clock's millisecond domain. Rejected or late tracks are stopped.
   *
   * @throws On supersession or deadline expiry. Acquisition and profile-verification
   *   failures become retryable capture-preparation-failed errors after attempts end.
   */
  async prepareVideo(constraints: MediaTrackConstraints,
    options: CaptureVideoOptions & { maxAcquisitionAttempts?: 1 | 2 } = {}): Promise<PreparedVideoCapture | null> {
    const generation = this.generation;
    const isCurrent = () => generation === this.generation && options.isCurrent?.() !== false;
    this.assertCurrent(isCurrent, options.hostDeadlineMs);
    const previousTrack = this.current;
    const profile = options.verifyProfile ?? captureFloorFromConstraints(constraints) ?? undefined;
    const deviceId = this.deviceId(constraints);
    if (previousTrack?.readyState === 'live' && deviceId && previousTrack.getSettings().deviceId === deviceId
      && trackMatchesVideoConstraints(previousTrack, constraints) && (!profile || trackMeetsCaptureProfile(previousTrack, profile))) {
      return this.register(previousTrack, previousTrack, generation);
    }
    const attempts = options.maxAcquisitionAttempts ?? (profile ? 2 : 1);
    let lastError: unknown;
    for (let attempt = 0; attempt < attempts; attempt++) {
      this.assertCurrent(isCurrent, options.hostDeadlineMs);
      const requested = attempt && profile ? this.retryConstraints(constraints, deviceId, options.shadowcamName, profile) : constraints;
      try {
        const track = await this.acquire(requested, isCurrent, options.hostDeadlineMs);
        if (!track) throw Object.assign(new Error('Capture produced no video track'), { code: 'no-video-track' });
        if (!isCurrent()) { this.stop(track); throw new Error('Capture superseded'); }
        // The retry only prefers a rate, so verify the exact size and any finite positive rate.
        const verifying = attempt && profile ? { ...profile, frameRate: 0 } : profile;
        if (verifying && !trackMeetsCaptureProfile(track, verifying)) {
          this.stop(track);
          throw new Error('Capture verify failed: settings outside requested profile');
        }
        return this.register(track, previousTrack, generation);
      } catch (error) {
        this.assertCurrent(isCurrent, options.hostDeadlineMs);
        lastError = error;
        if ((error as { code?: string })?.code === 'no-video-track') break;
      }
    }
    const error = new Error(lastError instanceof Error ? lastError.message : 'Video capture failed') as RetryableCapturePreparationError;
    error.code = 'capture-preparation-failed'; error.retryable = true;
    throw error;
  }

  commitPreparedVideo(prepared: PreparedVideoCapture, isCurrent?: () => boolean): MediaStreamTrack {
    const entry = this.prepared.get(prepared.id);
    if (!entry && this.committed.has(prepared) && this.current === prepared.track && isCurrent?.() !== false) return prepared.track;
    if (!entry || entry.value !== prepared || entry.generation !== this.generation || isCurrent?.() === false
      || prepared.track.readyState === 'ended') throw new Error('Capture superseded');
    if (this.current === prepared.track) {
      this.prepared.delete(prepared.id); this.committed.add(prepared); return prepared.track;
    }
    prepared.track.contentHint = 'motion';
    const previous = this.current;
    this.current = prepared.track;
    this.generation++;
    try { this.deps.changed(this.current); }
    catch (error) {
      this.current = previous; this.generation--;
      try { this.deps.changed(previous); } catch { /* Preserve the original commit failure. */ }
      throw error;
    }
    this.prepared.delete(prepared.id); this.committed.add(prepared);
    return this.current;
  }

  discardPreparedVideo(prepared: PreparedVideoCapture): void {
    if (this.prepared.get(prepared.id)?.value !== prepared) return;
    this.prepared.delete(prepared.id);
    if (this.current !== prepared.track) this.stop(prepared.track);
  }

  retireVideoTrack(track: MediaStreamTrack): void {
    if (track === this.current) throw new Error('Cannot retire current video track');
    this.stop(track);
  }

  ended(track: MediaStreamTrack): void {
    if (this.current !== track) return;
    this.current = null; this.generation++; this.deps.changed(null);
  }

  stopVideoTrack(): void {
    const track = this.current;
    this.current = null; this.generation++;
    if (track) this.stop(track);
    for (const { value } of this.prepared.values()) if (value.track !== track) this.stop(value.track);
    this.prepared.clear();
  }

  private register(track: MediaStreamTrack, previousTrack: MediaStreamTrack | null, generation: number): PreparedVideoCapture {
    const value = Object.freeze({ id: ++this.nextId, track, previousTrack });
    this.prepared.set(value.id, { value, generation });
    return value;
  }

  private acquire(constraints: MediaTrackConstraints, isCurrent: () => boolean, deadline?: number): Promise<MediaStreamTrack | null> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = deadline === undefined ? undefined : setTimeout(() => {
        settled = true; reject(new Error('Capture deadline exceeded'));
      }, Math.max(0, deadline - this.now()));
      void Promise.resolve().then(() => this.deps.acquire(constraints)).then(stream => {
        const tracks = stream.getVideoTracks();
        if (settled || !isCurrent() || (deadline !== undefined && this.now() >= deadline)) {
          tracks.forEach(track => this.stop(track));
          if (!settled) reject(new Error('Capture superseded or deadline exceeded'));
        } else {
          for (const track of tracks.slice(1)) this.stop(track);
          resolve(tracks[0] ?? null);
        }
        settled = true; if (timer !== undefined) clearTimeout(timer);
      }, error => { if (!settled) reject(error); settled = true; if (timer !== undefined) clearTimeout(timer); });
    });
  }

  private retryConstraints(original: MediaTrackConstraints, deviceId: string | undefined,
    shadowcam: string | undefined, profile: ShadowcamCaptureProfile): MediaTrackConstraints {
    if (shadowcam && deviceId) return videoConstraintsForShadowcam(deviceId, shadowcam, 'fallback');
    return { deviceId: deviceId ? { exact: deviceId } : original.deviceId,
      width: { exact: profile.width },
      height: { exact: profile.height },
      frameRate: { ideal: FALLBACK_CAPTURE_FPS } };
  }
  private deviceId(constraints: MediaTrackConstraints): string | undefined {
    const id = constraints.deviceId;
    return typeof id === 'string' ? id : id && typeof id === 'object' && 'exact' in id && typeof id.exact === 'string' ? id.exact : undefined;
  }
  private now(): number { return this.deps.nowMs?.() ?? Date.now(); }
  private assertCurrent(isCurrent: () => boolean, deadline?: number): void {
    if (!isCurrent()) throw new Error('Capture superseded');
    if (deadline !== undefined && this.now() >= deadline) throw new Error('Capture deadline exceeded');
  }
  private stop(track: MediaStreamTrack): void {
    if (this.stopped.has(track)) return;
    this.stopped.add(track); track.stop();
  }
}
